import { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import { Add, PhonelinkLockOutlined } from "@mui/icons-material";
import { SecaoConfiguracao } from "../../../../core/components/SecaoConfiguracao";
import { IMarketplace } from "../../../interfaces/IMarketplace";
import { IIntegracaoWhatsApp } from "../../../interfaces/IIntegracaoMarketplace";
import { useMarketplaces } from "../../../hooks/useMarketplaces";
import { useSnackbar } from "../../../../core/contexts/SnackbarContext";
import { useMascara } from "../../../../core/hooks/useMascara";
import { validarTelefone } from "../../../../core/utils/validarTelefone";

interface ConfigurarNumerosPermitidosWhatsAppProps {
    marketplace: IMarketplace;
    refresh: () => void;
}

const apenasDigitos = (telefone: string) => telefone.replace(/\D/g, "");

export const ConfigurarNumerosPermitidosWhatsApp = ({ marketplace, refresh }: ConfigurarNumerosPermitidosWhatsAppProps) => {
    const [novoNumero, setNovoNumero] = useState("");
    const { salvar, loading } = useMarketplaces();
    const notificar = useSnackbar();
    const { mascaraTelefone } = useMascara();

    const integracao = marketplace?.configuracao?.integracao as IIntegracaoWhatsApp;
    const restringirNumeros = integracao?.restringirNumeros ?? false;
    const numerosPermitidos = integracao?.numerosPermitidos ?? [];

    const persistirIntegracao = async (dadosIntegracao: Partial<IIntegracaoWhatsApp>) => {
        await salvar({
            ...marketplace,
            configuracao: {
                ...marketplace.configuracao,
                integracao: {
                    ...integracao,
                    ...dadosIntegracao
                }
            }
        });
        refresh();
    };

    const handleToggle = async (ativo: boolean) => {
        if (loading) return;
        await persistirIntegracao({ restringirNumeros: ativo });
    };

    const handleAdicionar = async () => {
        if (loading) return;

        const numero = novoNumero.trim();

        if (!validarTelefone(numero, true)) {
            return notificar("Informe um telefone válido, ex: (41) 99999-9999", { severity: "error" });
        }

        const jaExiste = numerosPermitidos.some(
            (n) => apenasDigitos(n) === apenasDigitos(numero)
        );

        if (jaExiste) {
            return notificar("Esse número já está na lista", { severity: "error" });
        }

        await persistirIntegracao({ numerosPermitidos: [...numerosPermitidos, numero] });
        setNovoNumero("");
    };

    const handleRemover = async (indexParaRemover: number) => {
        if (loading) return;
        const restantes = numerosPermitidos.filter((_, index) => index !== indexParaRemover);
        await persistirIntegracao({ numerosPermitidos: restantes });
    };

    return (
        <SecaoConfiguracao
            titulo="Restringir números"
            descricao="Processa apenas mensagens dos números cadastrados"
            icone={<PhonelinkLockOutlined />}
            corIcone={restringirNumeros ? undefined : "#9e9e9e"}
            acoes={
                <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                        loading ? (
                            <Box sx={{ display: "flex", alignItems: "center", px: 1.5 }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            <Switch
                                checked={restringirNumeros}
                                onChange={(e) => handleToggle(e.target.checked)}
                            />
                        )
                    }
                    label={
                        <Typography variant="body2" color="text.secondary">
                            {restringirNumeros ? "Ativa" : "Inativa"}
                        </Typography>
                    }
                    labelPlacement="start"
                />
            }
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="caption" color="text.secondary">
                    Quando ativa, o WhatsApp só processa mensagens recebidas dos números cadastrados abaixo.
                    Útil para testar sem impactar os demais clientes.
                </Typography>

                {restringirNumeros && numerosPermitidos.length === 0 && (
                    <Alert severity="warning" variant="outlined" sx={{ borderRadius: "12px" }}>
                        <Typography variant="caption">
                            A restrição está ativa mas nenhum número foi cadastrado — nenhuma mensagem será
                            processada. Adicione ao menos um número.
                        </Typography>
                    </Alert>
                )}

                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                    <TextField
                        label="Telefone"
                        size="small"
                        fullWidth
                        value={novoNumero}
                        onChange={(e) => setNovoNumero(mascaraTelefone(e.target.value))}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleAdicionar();
                            }
                        }}
                        placeholder="(41) 99999-9999"
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Add />}
                        onClick={handleAdicionar}
                        disabled={loading}
                        sx={{ whiteSpace: "nowrap", height: "40px" }}
                    >
                        Adicionar
                    </Button>
                </Box>

                {numerosPermitidos.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {numerosPermitidos.map((numero, index) => (
                            <Chip
                                key={index}
                                label={numero}
                                onDelete={loading ? undefined : () => handleRemover(index)}
                                disabled={loading}
                            />
                        ))}
                    </Box>
                ) : (
                    <Box sx={{
                        minHeight: "80px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        alignItems: "center",
                        justifyContent: "center",
                        border: theme => `1px dashed ${theme.palette.divider}`,
                        borderRadius: "12px",
                        p: 2
                    }}>
                        <Typography variant="body2" fontWeight={500}>Nenhum número cadastrado</Typography>
                        <Typography variant="caption" color="text.secondary" textAlign="center">
                            Sem números cadastrados a restrição bloqueia todas as mensagens
                        </Typography>
                    </Box>
                )}
            </Box>
        </SecaoConfiguracao>
    );
};
